import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { agenteAtencionAPI } from '../../services/api';
import { Loader2, Bot, User as UserIcon, LifeBuoy, Home, Star } from 'lucide-react';
import AsistenciaBadge from './AsistenciaBadge';

// P13C — Esqueleto del Agente 2: conversación DETERMINISTA (guion cerrado, sin IA ni LLM).
// Perfila al cliente, lo registra en el CRM y muestra coincidencias explicables de la sección.
export const AgenteAtencion = ({ asesores, seccionActual, onCerrar, onGuardado }) => {
  const { toast } = useToast();
  const [guion, setGuion] = useState(null);
  const [asesorId, setAsesorId] = useState('');
  const [sesion, setSesion] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [valor, setValor] = useState('');
  const [cargando, setCargando] = useState(false);
  const [coincidencias, setCoincidencias] = useState(null);
  const finRef = useRef(null);

  useEffect(() => { agenteAtencionAPI.guion().then(setGuion).catch(() => {}); }, []);
  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes]);

  const disponibles = seccionActual
    ? asesores.filter((a) => a.seccion_id === seccionActual) : asesores;
  const paso = sesion?.paso_actual || null;
  const error = (e) => toast({ title: 'Acción rechazada', description: e.message,
    variant: 'destructive' });

  const aplicar = (d, textoUsuario) => {
    setSesion(d);
    setValor('');
    setMensajes((m) => [
      ...m,
      ...(textoUsuario ? [{ de: 'humano', texto: textoUsuario }] : []),
      ...(d.mensaje_agente ? [{ de: 'agente', texto: d.mensaje_agente }] : []),
    ]);
  };

  const iniciar = async () => {
    setCargando(true);
    try {
      const d = await agenteAtencionAPI.crearSesion({ asesor_id: asesorId, canal: 'web_dev',
        consentimiento: true, origen: 'agente_atencion_dev' });
      setMensajes([{ de: 'agente', texto: guion?.presentacion || '' }]);
      aplicar(d, null);
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const responder = async (omitir) => {
    if (!paso) return;
    setCargando(true);
    try {
      const d = await agenteAtencionAPI.responder(sesion.sesion_id, {
        paso: paso.clave, valor: omitir ? null : valor,
        idempotency_key: `${sesion.sesion_id}:${paso.clave}:${sesion.interacciones}`,
      });
      aplicar(d, omitir ? '(omitido)' : String(valor));
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const confirmar = async () => {
    setCargando(true);
    try {
      const d = await agenteAtencionAPI.confirmar(sesion.sesion_id, {
        idempotency_key: `${sesion.sesion_id}:confirmar`,
      });
      setSesion(d.sesion);
      setCoincidencias(d.coincidencias);
      setMensajes((m) => [...m, { de: 'agente',
        texto: `Cliente registrado. Encontré ${d.coincidencias?.coincidencias ?? 0} coincidencia(s) sobre ${d.coincidencias?.inventario_evaluado ?? 0} inmueble(s) de la sección.` }]);
      toast({ title: 'Cliente registrado', description: `${d.cliente}` });
      onGuardado();
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const registrarInteres = async (propiedadId, nombre) => {
    setCargando(true);
    try {
      const d = await agenteAtencionAPI.interes(sesion.sesion_id, { propiedad_id: propiedadId });
      toast({ title: d.creada ? 'Oportunidad creada' : 'Oportunidad existente',
        description: `${nombre} · etapa ${d.etapa}` });
      onGuardado();
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const pedirHumano = async () => {
    setCargando(true);
    try {
      const d = await agenteAtencionAPI.handoff(sesion.sesion_id,
        { motivo: 'El cliente solicita apoyo humano para continuar' });
      aplicar(d, 'Necesito apoyo humano');
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="agente-atencion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" /> Agente de atención a clientes
            <Badge className="bg-slate-100 text-slate-700">esqueleto determinista</Badge>
            <AsistenciaBadge />
          </DialogTitle>
        </DialogHeader>

        {!sesion && (
          <div className="space-y-3" data-testid="agente-atencion-inicio">
            <p className="text-sm text-gray-600">{guion?.presentacion}</p>
            <select value={asesorId} onChange={(e) => setAsesorId(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white w-full"
                    data-testid="agente-atencion-asesor">
              <option value="">Asesor responsable del cliente…</option>
              {disponibles.map((a) => (
                <option key={a.asesor_id} value={a.asesor_id}>{a.nombre}</option>
              ))}
            </select>
            <Button onClick={iniciar} disabled={!asesorId || cargando}
                    data-testid="agente-atencion-iniciar-btn">
              {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Iniciar conversación
            </Button>
          </div>
        )}

        {sesion && (
          <div className="space-y-4" data-testid="agente-atencion-conversacion">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-indigo-100 text-indigo-800"
                     data-testid="agente-atencion-estado">{sesion.estado}</Badge>
              <span className="text-gray-500" data-testid="agente-atencion-progreso">
                {sesion.progreso.respondidos}/{sesion.progreso.total_pasos} datos ·
                {' '}{sesion.progreso.requeridos_pendientes.length} obligatorio(s) pendiente(s)
              </span>
              {sesion.handoff_humano && (
                <Badge className="bg-amber-100 text-amber-800"
                       data-testid="agente-atencion-handoff">apoyo humano solicitado</Badge>
              )}
            </div>

            <div className="border rounded-xl p-3 space-y-3 max-h-60 overflow-y-auto bg-gray-50">
              {mensajes.filter((m) => m.texto).map((m, i) => (
                <div key={i} className={`flex gap-2 text-sm ${m.de === 'humano'
                  ? 'justify-end text-right' : ''}`}>
                  {m.de === 'agente' && <Bot className="h-4 w-4 mt-1 text-indigo-600 shrink-0" />}
                  <span className={`rounded-2xl px-3 py-2 max-w-[80%] ${m.de === 'humano'
                    ? 'bg-indigo-600 text-white' : 'bg-white border'}`}>{m.texto}</span>
                  {m.de === 'humano' && <UserIcon className="h-4 w-4 mt-1 text-gray-400 shrink-0" />}
                </div>
              ))}
              <div ref={finRef} />
            </div>

            {paso && (
              <div className="flex flex-wrap items-center gap-2">
                {paso.opciones
                  ? (
                    <select value={valor} onChange={(e) => setValor(e.target.value)}
                            className="border rounded-lg px-3 py-2 text-sm bg-white flex-1"
                            data-testid="agente-atencion-valor">
                      <option value="">Elige una opción…</option>
                      {paso.opciones.map((o) => (
                        <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  )
                  : (
                    <Input value={valor} className="flex-1"
                           type={['entero', 'decimal', 'moneda'].includes(paso.tipo)
                             ? 'number' : 'text'}
                           placeholder={paso.tipo === 'lista'
                             ? 'Separadas por coma' : 'Tu respuesta'}
                           onChange={(e) => setValor(e.target.value)}
                           data-testid="agente-atencion-valor" />
                  )}
                <Button onClick={() => responder(false)} disabled={cargando || valor === ''}
                        data-testid="agente-atencion-responder-btn">
                  {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Responder
                </Button>
                {!paso.requerido && (
                  <Button variant="outline" onClick={() => responder(true)} disabled={cargando}
                          data-testid="agente-atencion-omitir-btn">Omitir</Button>
                )}
                <Button variant="ghost" onClick={pedirHumano} disabled={cargando}
                        data-testid="agente-atencion-handoff-btn">
                  <LifeBuoy className="h-4 w-4 mr-2" />Apoyo humano
                </Button>
              </div>
            )}

            {sesion.estado === 'lista_para_confirmar' && (
              <Button onClick={confirmar} disabled={cargando}
                      data-testid="agente-atencion-confirmar-btn">
                {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Registrar cliente y ver coincidencias
              </Button>
            )}

            {coincidencias && (
              <div className="space-y-2" data-testid="agente-atencion-coincidencias">
                <p className="text-sm font-medium text-gray-700">
                  Coincidencias explicables ({coincidencias.coincidencias ?? 0} de{' '}
                  {coincidencias.total ?? 0} evaluadas) · umbral {coincidencias.umbral}
                </p>
                {(coincidencias.items || []).length === 0 && (
                  <p className="text-sm text-gray-500">
                    No hay inventario disponible que coincida en esta sección. Ofrece apoyo humano.
                  </p>
                )}
                {(coincidencias.items || []).map((x) => (
                  <div key={x.propiedad_id} className="border rounded-xl p-3 text-sm bg-white"
                       data-testid={`agente-atencion-match-${x.propiedad_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium flex items-center gap-1">
                        <Home className="h-4 w-4 text-indigo-600" />{x.nombre}
                      </span>
                      <Badge className={x.coincide ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'}>
                        <Star className="h-3 w-3 mr-1" />{x.puntaje}
                      </Badge>
                    </div>
                    <p className="text-gray-500 mt-1">
                      {x.tipo} · {x.operacion} · ${Number(x.precio || 0).toLocaleString()} {x.moneda}
                      {x.colonia ? ` · ${x.colonia}` : ''}{x.ciudad ? `, ${x.ciudad}` : ''}
                      {x.recamaras ? ` · ${x.recamaras} rec` : ''}
                    </p>
                    {(x.razones || []).length > 0 && (
                      <ul className="text-xs text-gray-500 list-disc pl-5 mt-1">
                        {x.razones.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                    <Button size="sm" variant="outline" className="mt-2" disabled={cargando}
                            onClick={() => registrarInteres(x.propiedad_id, x.nombre)}
                            data-testid={`agente-atencion-interes-${x.propiedad_id}`}>
                      Registrar interés (crear oportunidad)
                    </Button>
                  </div>
                ))}
                <Button variant="outline" onClick={onCerrar}
                        data-testid="agente-atencion-cerrar-btn">Cerrar</Button>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Guion determinista: sin IA generativa, sin claves de modelos, sin WhatsApp. Sólo se
              muestra inventario autorizado de la sección, con el motivo de cada coincidencia; nunca
              se inventan propiedades ni se revela la dirección exacta.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgenteAtencion;
